import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import StoreShell from "@/components/landing/StoreShell";
import { BLOG_I18N, urduProps } from "@/content/blogI18n";
import { useSeo, SEO_KEYWORDS } from "@/hooks/useSeo";

export const LangToggle = ({ to, label, className, testid }) => (
    <Link
        to={to}
        data-testid={testid}
        dir="ltr"
        style={{ letterSpacing: 0 }}
        className={`${className} text-sm font-bold text-forest-700 border border-forest-200 rounded-full px-4 py-1 hover:bg-forest-800 hover:text-gold-400 transition-colors`}
    >
        {label}
    </Link>
);

export default function BlogIndexPage({ lang = "en" }) {
    const t = BLOG_I18N[lang];
    const { className: langClass, ...langAttrs } = urduProps(lang);

    useSeo({
        title: t.title,
        description: t.description,
        keywords: SEO_KEYWORDS,
        path: t.base,
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "Resham Drops Hair Care Blog",
            inLanguage: t.locale,
            blogPost: t.posts.map((p) => ({
                "@type": "BlogPosting",
                headline: p.title,
                url: `${window.location.origin}${t.base}/${p.slug}`,
                datePublished: p.date,
            })),
        },
    });

    return (
        <StoreShell testid={lang === "ur" ? "ur-blog-index-page" : "blog-index-page"}>
            <main {...langAttrs} className={`${langClass} max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20`}>
                <div className="flex items-center justify-between gap-4">
                    <span className="inline-block bg-forest-800 text-gold-400 text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest">
                        {t.badge}
                    </span>
                    <LangToggle to={t.other} label={t.otherLabel} className={t.otherClass} testid="blog-lang-toggle" />
                </div>
                <h1 className="font-serif font-bold text-forest-800 text-4xl sm:text-5xl lg:text-6xl leading-tight mt-4">{t.h1}</h1>
                <p className="text-forest-900/70 text-sm sm:text-base mt-5 max-w-2xl">{t.intro}</p>
                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
                    {t.posts.map((p) => (
                        <Link
                            key={p.slug}
                            to={`${t.base}/${p.slug}`}
                            data-testid={`blog-card-${p.slug}`}
                            className="group bg-white rounded-2xl border border-forest-100 shadow-sm p-6 sm:p-7 hover:border-gold-500/60 hover:-translate-y-0.5 transition-[transform,border-color] duration-300"
                        >
                            <div className="flex items-center gap-2 text-[11px] text-forest-700/70 font-semibold uppercase tracking-widest">
                                <Clock className="w-3.5 h-3.5" /> {t.mins(p.readMins)}
                            </div>
                            <h2 className="font-serif font-bold text-forest-800 text-base md:text-lg mt-3 leading-snug">{p.title}</h2>
                            <p className="text-sm text-forest-900/70 mt-2 leading-relaxed">{p.description}</p>
                            <span className="inline-flex items-center gap-1.5 text-sm font-bold text-gold-600 mt-4">
                                {t.read} <ArrowRight className={`w-4 h-4 transition-transform group-hover:translate-x-1 ${lang === "ur" ? "rotate-180" : ""}`} />
                            </span>
                        </Link>
                    ))}
                </div>
            </main>
        </StoreShell>
    );
}
